import { COMPETITION_SETUP_ROLES, type RoleCode, SPORT_CLASS_SORT_KEYS, type SportClassItem, type SportClassSortKey } from "@seagames/contracts";
import { type ColumnDef, DataTable, ListHeader, ListTag, ListToolbar, PrimaryCell, StatsStrip, useUiStrings } from "@seagames/ui";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { z } from "zod";
import { ConfirmDeleteModal } from "../../components/common/confirm-delete-modal";
import { EditDeleteActions } from "../../components/common/edit-delete-actions";
import { usageColumn } from "../../components/common/list-columns";
import { gamesChipGroup } from "../../components/competition/list-chips";
import { type SportClassFormTarget, SportClassFormModal } from "../../components/sport-classes/sport-class-form-modal";
import { ApiError } from "../../lib/api/client";
import { requireAdminRole } from "../../lib/auth/guards";
import { gamesOptionsOptions, pickDefaultGames } from "../../lib/competition/games";
import { listSearch } from "../../lib/list-page/list-search";
import { useConfirmDelete } from "../../lib/list-page/use-confirm-delete";
import { sortSelect } from "../../lib/list-page/sort-select";
import { useListPage } from "../../lib/list-page/use-list-page";
import { deleteSportClass, invalidateSportClasses, sportClassStatsOptions, sportClassesListOptions } from "../../lib/sport-classes/queries";
import { sportOptionsOptions } from "../../lib/sports/queries";

/**
 * Settings › Para classes. `GET /sport-classes` filters by `sport_id` only;
 * `games_id` lives in the URL purely to scope the sport picker (and the add
 * form's sport list) — it is never sent to the list endpoint.
 */
export const Route = createFileRoute("/_app/settings/sport-classes")({
	beforeLoad: ({ context }) => requireAdminRole(context.session),
	validateSearch: listSearch(SPORT_CLASS_SORT_KEYS, { games_id: z.string().uuid(), sport_id: z.string().uuid() }),
	component: SportClassesPage,
});

interface SportClassFilters extends Record<string, unknown> {
	search: string;
	/** `""` = the page's default games (the para games). */
	games_id: string;
	sport_id: string;
}
const EMPTY_FILTERS: SportClassFilters = { search: "", games_id: "", sport_id: "" };

/** Para classes belong to the para games, so that is this page's default — unlike the other pages' main games. */
function useParaGamesId(): string | undefined {
	const games = useQuery(gamesOptionsOptions());
	return games.data ? (games.data.find((g) => g.is_para) ?? pickDefaultGames(games.data))?.games_id : undefined;
}

/** Turn a 409 CONFLICT into the page's own "in use" sentence; anything else passes through to the toast as is. */
const inUseAs = (message: string) => (err: unknown) => {
	throw err instanceof ApiError && err.code === "CONFLICT" ? new Error(message) : err;
};

function SportClassesPage() {
	const { t } = useTranslation("sportClasses");
	const { t: tc } = useTranslation("common");
	const { formatNumber } = useUiStrings();
	const { session } = Route.useRouteContext();
	const search = Route.useSearch();
	const navigate = Route.useNavigate();
	const canWrite = session.active_role !== null && (COMPETITION_SETUP_ROLES as readonly RoleCode[]).includes(session.active_role);
	const [form, setForm] = useState<SportClassFormTarget | null>(null);
	const defaultGamesId = useParaGamesId();

	const page = useListPage({
		search,
		navigate,
		listOptions: (q) => sportClassesListOptions({ ...q, sport_id: search.sport_id }),
		columnsKey: "sport-classes",
		filters: {
			empty: EMPTY_FILTERS,
			fromUrl: (s) => ({ search: s.search ?? "", games_id: s.games_id ?? "", sport_id: s.sport_id ?? "" }),
			toUrl: (f) => ({
				search: f.search.trim() || undefined,
				// The default games stays out of the URL, so a plain link shows it.
				games_id: f.games_id === "" || f.games_id === defaultGamesId ? undefined : f.games_id,
				sport_id: f.sport_id || undefined,
			}),
		},
		summary: { range: (v) => t("pagination.summary", v), empty: t("pagination.empty") },
	});

	/** The games the sport chip lists, and the add form uses: the URL's, else the para games. */
	const gamesId = search.games_id ?? defaultGamesId;
	const games = useQuery(gamesOptionsOptions());
	const sports = useQuery({ ...sportOptionsOptions(gamesId ?? ""), enabled: gamesId !== undefined });
	// One games' classes per sport (largest first) — the Sport chip's counts and the strip, one aggregate statement.
	const stats = useQuery({ ...sportClassStatsOptions(gamesId ?? ""), enabled: gamesId !== undefined });
	const sportOptions = useMemo(() => {
		const countOf = new Map(stats.data?.by_sport.map((b) => [b.sport_id, b.count]));
		return (sports.data ?? []).map((s) => ({ value: s.sport_id, label: s.name, count: stats.data ? (countOf.get(s.sport_id) ?? 0) : undefined }));
	}, [sports.data, stats.data]);

	const del = useConfirmDelete<SportClassItem>({
		// 409 = an event is open to it or an athlete competes in it; the API sends no details, so the copy is ours.
		remove: (r) => deleteSportClass(r.sport_class_id, `Deleted para class ${r.code} of ${r.sport_code}`).catch(inUseAs(t("errors.inUse"))),
		invalidate: invalidateSportClasses,
		deleted: (r) => t("delete.deleted", { name: r.code }),
	});

	const total = page.list.data?.total;
	const sort = sortSelect<SportClassSortKey>(
		search,
		"code:asc",
		[
			{ value: "code:asc", label: tc("sort.codeAsc") },
			{ value: "code:desc", label: tc("sort.codeDesc") },
			{ value: "name:asc", label: tc("sort.nameAsc") },
			{ value: "name:desc", label: tc("sort.nameDesc") },
			{ value: "sport:asc", label: t("sort.sportAsc") },
			{ value: "sport:desc", label: t("sort.sportDesc") },
		],
		page.patch,
	);

	const columns: ColumnDef<SportClassItem, SportClassSortKey>[] = [
		// A para class is known by its code (S9, T11); the name is optional, so it is the sub-line.
		{
			key: "code",
			header: t("table.code"),
			sortable: true,
			lockVisible: true,
			minWidth: "14rem",
			cell: (r) => (
				<PrimaryCell sub={r.name ?? undefined} title={r.name ?? undefined}>
					<span className="font-mono tracking-[0.06em]">{r.code}</span>
				</PrimaryCell>
			),
		},
		{ key: "sport", header: t("table.sport"), sortable: true, minWidth: "7rem", cell: (r) => <ListTag className="font-mono tracking-[0.08em]">{r.sport_code}</ListTag> },
		usageColumn("events_count", t("table.events"), t("table.eventsTitle")),
	];

	return (
		<div className="flex min-w-0 flex-col gap-[1.125rem]">
			<ListHeader
				title={t("page.title")}
				count={total === undefined ? undefined : t("page.count", { count: total, n: formatNumber(total) })}
				description={<Trans t={t} i18nKey="page.intro" components={{ strong: <strong className="font-semibold text-gms-ink-2" /> }} />}
				actionLabel={t("page.addSportClass")}
				onAction={canWrite ? () => setForm({ mode: "create", sportId: search.sport_id }) : undefined}
			/>

			<StatsStrip
				label={tc("list.summary")}
				// The list spans every games (it filters by sport, not games); the second cell is the picked games' share.
				cells={[
					{ key: "total", label: t("stats.total"), value: total ?? "—", note: page.hasFilters ? t("stats.matching") : undefined },
					...(stats.data ? [{ key: "in-games", label: t("stats.inGames"), value: stats.data.total }] : []),
				]}
			/>

			<ListToolbar
				search={search.search ?? ""}
				onSearch={(v) => page.patch({ search: v || undefined })}
				searchPlaceholder={t("filters.searchPlaceholder")}
				sort={sort}
				chips={[
					// The list filters by sport only; the games just scopes the sport menu. A new games empties the sport.
					gamesChipGroup(games.data, gamesId, (id) => page.patch({ games_id: id === defaultGamesId ? undefined : id, sport_id: undefined }), t("filters.gamesLabel")),
					{
						kind: "menu",
						key: "sport",
						label: t("filters.sportLabel"),
						options: sportOptions,
						value: search.sport_id ?? null,
						onChange: (v) => page.patch({ sport_id: v ?? undefined }),
						searchPlaceholder: t("filters.searchSports"),
						disabled: gamesId === undefined || sports.isPending,
					},
				]}
				onClearAll={page.clearFilters}
				active={page.hasFilters}
			/>

			<DataTable<SportClassItem, SportClassSortKey>
				{...page.table}
				variant="list"
				columns={columns}
				rowKey={(r) => r.sport_class_id}
				empty={page.emptyState({
					filtered: { title: t("empty.filteredTitle"), description: t("empty.filteredDescription") },
					none: { title: t("empty.noneTitle"), description: t("empty.noneDescription") },
					clearLabel: t("empty.clearFilters"),
				})}
				rowActions={(r) => (
					<EditDeleteActions
						editLabel={t("table.editLabel", { name: r.code })}
						onEdit={canWrite ? () => setForm({ mode: "edit", sportClass: r }) : undefined}
						deleteLabel={t("table.deleteLabel", { name: r.code })}
						onDelete={canWrite ? () => del.ask(r) : undefined}
						canDelete={r.events_count === 0}
						variant="list"
					/>
				)}
			/>

			{canWrite && <SportClassFormModal target={form} gamesId={gamesId} onClose={() => setForm(null)} />}
			{canWrite && (
				<ConfirmDeleteModal
					{...del.modal}
					title={t("delete.title")}
					description={<Trans t={t} i18nKey="delete.description" values={{ name: del.row?.code ?? "" }} components={{ strong: <strong className="font-semibold text-base-content" /> }} />}
					confirmLabel={t("delete.confirm")}
					errorPrefix={t("delete.failed")}
				/>
			)}
		</div>
	);
}
